/**
 * Alpine.js Report Modal Component
 *
 * Global modal for reporting content (users, photos, messages, posts, etc.).
 * Triggered by window.openReportModal(entityType, targetId, targetUid).
 *
 * Usage:
 *   window.openReportModal('user', userId, userId)
 *   window.openReportModal('photo', photoId, photoOwnerId)
 *   window.openReportModal('message', messageId, senderId)
 *   window.openReportModal('post', postId, posterUid)
 */

import { REPORT_FORM_ORIGIN } from './report-origin.js';

import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';

function submittedMessage() {
    return window.tOr('legacy.report.submitted', 'Report submitted. Thank you for helping keep InterPals safe.');
}

export function reportModal() {
    return {
        isOpen: false,
        entityType: '',
        targetId: '',
        targetUid: '',
        reasonSel: '',
        reason: '',
        isSubmitting: false,
        step: 'select',
        _titles: {},
        _generation: 0,

        init() {
            const titlesEl = this.$el.querySelector('script[data-report-titles]');
            this._titles = titlesEl ? JSON.parse(titlesEl.textContent) : {};
            window.openReportModal = (entityType, targetId, targetUid) => {
                this.open(entityType, targetId, targetUid);
            };
            window.closeReportModal = () => this.dismiss();
        },

        open(entityType, targetId, targetUid) {
            this._generation += 1;
            this.entityType = entityType;
            this.targetId = String(targetId);
            this.targetUid = String(targetUid || targetId);
            this.reasonSel = '';
            this.reason = '';
            this.step = 'select';
            this.isSubmitting = false;
            this.isOpen = true;
            window._reportModalOpen = true;
            const titleEl = document.getElementById('report-modal-title');
            if (titleEl) titleEl.textContent = this._titles[entityType] || this._titles['user'] || '';
            lockBodyScroll();
        },

        close(event) {
            if (!this.isOpen) return;
            if (event?.key === 'Escape') event.preventDefault();
            this.isOpen = false;
            window._reportModalOpen = false;
            unlockBodyScroll();
        },

        // The address left the reported entity. A submit in flight for it must not report back.
        dismiss() {
            this._generation += 1;
            this.isSubmitting = false;
            this.close();
        },

        selectReason(reason) {
            this.reasonSel = reason;
            this.step = 'detail';
        },

        async submit() {
            if (this.isSubmitting || !this.reasonSel) return;
            this.isSubmitting = true;
            // A submit that outlives its dialog must not close or unlock the next one.
            const generation = this._generation;

            try {
                const token = await window.getApiToken();
                if (generation !== this._generation) return;
                if (!token) throw new Error(window.tOr('common.auth_required', 'Authentication required'));

                const body = new URLSearchParams({
                    entity_type: this.entityType,
                    target_id: this.targetId,
                    target_uid: this.targetUid,
                    reason_sel: this.reasonSel,
                    // Stated rather than left to a default: the server records
                    // an unstated origin as unknown, so a surface that says
                    // nothing drops out of the distinction instead of landing
                    // on the deliberate half of it.
                    origin: REPORT_FORM_ORIGIN,
                });
                if (this.reason.trim()) {
                    body.set('reason', this.reason.trim());
                }

                const response = await fetch('/v1/report', {
                    method: 'POST',
                    headers: {
                        'X-AUTH-TOKEN': token,
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    credentials: 'include',
                    body,
                });

                if (generation !== this._generation) {
                    // The report landed on the entity of the dismissed dialog. Its page-wide toast is still accurate.
                    if (response.ok) window.showSuccess?.(submittedMessage());
                    return;
                }
                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    if (generation !== this._generation) return;

                    // The reporter already reported this entity inside the
                    // re-report cooldown. Nothing is wrong on their side, so
                    // dismiss the modal and reassure rather than showing an
                    // error they cannot act on.
                    if (data.error?.code === 'report_duplicate') {
                        this.close();
                        window.showSuccess?.(data.error.message
                            || window.tOr('legacy.report.recent_duplicate', "You already reported this. We're looking into it."));
                        return;
                    }

                    throw new Error(data.error?.message || window.tOr('common.request_failed', 'Request failed'));
                }

                this.close();
                window.showSuccess?.(submittedMessage());
            } catch (error) {
                if (generation !== this._generation) return;
                window.showError?.(error.message || window.tOr('legacy.report.submit_failed', 'Failed to submit report'));
            } finally {
                if (generation === this._generation) this.isSubmitting = false;
            }
        },
    };
}

if (typeof window !== 'undefined') {
    window.reportModal = reportModal;
}

export default reportModal;
